import { CollectionViewer, DataSource } from "@angular/cdk/collections";
import {
  Observable,
  BehaviorSubject,
  of,
  Subscription,
  combineLatest
} from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import {
  ODataResponse,
  DataTableService
} from "../_services/data-table.service";
import { ITableParams } from "../components/data-table/table-params";
import { HttpMethod } from "../enum";
import { formUtil, IReactiveFormsDeepMappingOptions } from "../util/form-util";
import { FormGroup } from "@angular/forms";
import { Guid } from "../util/Guid";
import { HttpBadRequestResponse, LxmMessage } from "../_helpers";

export class Group {
  level = 0;
  parent: Group;
  expanded = true;
  groupFor: string;
  groupId?: string;
  totalCounts = 0;
  get visible(): boolean {
    return !this.parent || (this.parent.visible && this.parent.expanded);
  }
}

export interface IGroupByColumn {
  key: string;
  label: string;
}

export class DataTableDataSource<TItem, TData> implements DataSource<TItem> {
  private _dataSubject = new BehaviorSubject<TItem[]>([]);
  private _scrollDataSubject = new BehaviorSubject<TItem[]>([]);
  private _countSubject = new BehaviorSubject<number>(0);
  private _loadingSubject = new BehaviorSubject<boolean>(false);
  private _searchKeySubject = new BehaviorSubject<string>(null);
  private _pageSubject = new BehaviorSubject<number>(null);
  private _pageSizeSubject = new BehaviorSubject<number>(null);
  private _sortSubject = new BehaviorSubject<string>(null);
  private _tableFilterSubject = new BehaviorSubject({
    criteria: null,
    searchTerm: null
  });

  private _loadSubscription: Subscription;

  public get loading$() {
    return this._loadingSubject.asObservable();
  }

  public get count$() {
    return this._countSubject.asObservable();
  }

  public get page$() {
    return this._pageSubject.asObservable();
  }

  public get pageSize$() {
    return this._pageSizeSubject.asObservable();
  }

  public get sort$() {
    return this._sortSubject.asObservable();
  }

  public get data$() {
    return this._dataSubject.asObservable();
  }

  public get data() {
    return this._dataSubject.value;
  }

  public get searchKey$() {
    return this._searchKeySubject.asObservable();
  }

  public get scrollData$() {
    return this._scrollDataSubject.asObservable();
  }

  public get scrollData() {
    return this._scrollDataSubject.value;
  }

  public get count() {
    return this._countSubject.value;
  }

  public get isLoading() {
    return this._loadingSubject.value;
  }

  constructor(
    private _dataService: DataTableService<TItem, TData>,
    private _method: HttpMethod,
    private _message: LxmMessage
  ) { }

  public connect(collectionViewer: CollectionViewer): Observable<TItem[]> {
    return combineLatest([this._dataSubject, this._tableFilterSubject]).pipe(
      map((latestValues) => {
        const [data, filterData] = latestValues;

        if (!filterData.criteria || !filterData.searchTerm) {
          return data;
        }

        return data.filter((item: any) => {
          return item[filterData.criteria]
            ?.toString()
            .toLocaleLowerCase()
            .includes(filterData.searchTerm.toString().toLocaleLowerCase());
        });
      })
    );
    // return this._dataSubject.asObservable();
  }

  public disconnect(collectionViewer: CollectionViewer): void {
    this._dataSubject.complete();
    this._loadingSubject.complete();
  }

  public prepareItems(
    items,
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupBy
  ) {
    const data = this.toItemType(items, formMappingOptions);
    return groupBy ? this.addGroups(data, groupBy) : data;
  }

  private toItemType(
    items: any[],
    formMappingOptions: IReactiveFormsDeepMappingOptions
  ): any[] {
    if (!formMappingOptions) return items;
    if (!items || items.length === 0) return items;
    if (items[0] instanceof FormGroup) return items;
    return (
      formUtil.createFormArray(items, null, formMappingOptions)?.controls ||
      items
    );
  }

  public pushItem(
    item: any,
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupBy = null
  ) {
    let dataItem = item;
    let firstArrayItem = this.data[0];

    // Convert item to FormGroup if data consists of FormGroups
    if (firstArrayItem instanceof FormGroup) {
      if (!(item instanceof FormGroup)) {
        dataItem = formUtil.createFormGroup(dataItem, null, formMappingOptions);
      }
    }

    this.data.push(dataItem);
    this.updateData(this.data, formMappingOptions, groupBy);
  }

  // GROUPS

  uniqueBy(a, key) {
    const seen = {};
    return a.filter((item) => {
      const k = key(item);
      return seen.hasOwnProperty(k) ? false : (seen[k] = true);
    });
  }

  getSublevel(
    data: any[],
    level: number,
    groupByColumns: any[],
    parent: Group
  ): any[] {
    if (level >= groupByColumns.length) {
      return data;
    }

    const refGroups = this.uniqueBy(
      data.map((row) => {
        let result;
        if (this.findExistingGroupById(data, row.groupId)) {
          result = row;
        } else {
          result = new Group();
          result.level = level + 1;
          result.parent = parent;
          result.groupId = Guid.newGuid().toString();

          for (let i = 0; i <= level; i++) {
            const groupFor = groupByColumns[i].key;
            result[groupFor] = row[groupFor];
            result.groupFor = groupFor;
          }
        }

        return result;
      }),
      JSON.stringify
    );

    const currentColumn = groupByColumns[level];
    let subGroups = [];
    refGroups.forEach((group) => {
      const rowsInGroup = data.filter(
        (row) => group[currentColumn.key] === row[currentColumn.key]
      );
      group.totalCounts = rowsInGroup.length;
      const subGroup = this.getSublevel(
        rowsInGroup,
        level + 1,
        groupByColumns,
        group
      );
      subGroup.unshift(group);
      subGroups = subGroups.concat(subGroup);
    });
    return subGroups;
  }

  addGroups(data: any[], groupByColumns: IGroupByColumn[]): any[] {
    if (!groupByColumns || groupByColumns.length === 0 || !data) {
      return data;
    }
    const sortByKeys = groupByColumns.map((col) => col.key);
    const getGroupValue = (item: any, key: string) => this._getItemGroupValue(item, key);
    const sortedData = this._multiSort(data, sortByKeys, getGroupValue);

    return this.iterativeGroup(sortedData, groupByColumns);
  }
  private findExistingGroupById(data: any[], groupId: string): Group[] {
    if (!data?.length || !groupId) return null;
    return data.find((item) => item.groupId === groupId);
  }

  //

  public loadData(
    dataUrl: string,
    data: TData,
    tableParams: ITableParams,
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupBy,
    onSuccess = () => { },
    isScrollData = false
  ): void {
    this._loadingSubject.next(true);

    if (this._loadSubscription) {
      this._loadSubscription.unsubscribe();
    }

    // console.log('DataTableDataSource.loadData: Requesting data with tableParams:', tableParams);

    this._dataService
      .get(dataUrl, data, tableParams, this._method)
      .pipe(
        catchError((e) => {
          const badReqResponse = e as HttpBadRequestResponse;
          if (badReqResponse && badReqResponse.validationResults?.searchTerm) {
            throw badReqResponse;
          }
          return of(new ODataResponse<TItem>());
        }),
        finalize(() => this._loadingSubject.next(false))
      )
      .subscribe((response) => {
        // console.log('DataTableDataSource.loadData: Received response:', response);
        if (!response) {
          this._countSubject.next(0);
          this._searchKeySubject.next(null);
          this._dataSubject.next(new Array<TItem>());
          return;
        }

        this._countSubject.next(response.count);
        this._searchKeySubject.next(response.searchKey);
        this._pageSubject.next(response.page);
        this._pageSizeSubject.next(response.pageSize);
        this._sortSubject.next(response.sort);

        const items = this.prepareItems(
          response.items,
          formMappingOptions,
          groupBy
        );

        if (isScrollData) {
          this._mergeScrolledData(response.items, formMappingOptions, groupBy);
        } else {
          this._dataSubject.next(items);
        }

        onSuccess();
      },
        error => {
          const badReqResponse = error as HttpBadRequestResponse;
          const message = badReqResponse.validationResults?.searchTerm[0]?.value;
          this._message.error({
            message: message
          });
        }
      );
  }

  public setData(
    data: ODataResponse<TItem>,
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupBy
  ) {
    this._countSubject.next(data.count || 0);
    this._searchKeySubject.next(data.searchKey);
    this._pageSubject.next(data.page);
    this._pageSizeSubject.next(data.pageSize);
    this._sortSubject.next(data.sort);
    this._dataSubject.next(
      this.prepareItems(data.items, formMappingOptions, groupBy)
    );
    this._loadingSubject.next(false);
  }

  public updateData(
    data: TItem[],
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupBy
  ) {
    const items = this.prepareItems(data, formMappingOptions, groupBy);
    this._countSubject.next(data.length);
    this._dataSubject.next(items);
    this._loadingSubject.next(false);
  }

  public updateRawData(
    data: TItem[],
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupBy
  ) {
    this._countSubject.next(data.length);
    this._dataSubject.next(
      this.prepareItems(data, formMappingOptions, groupBy)
    );
    this._loadingSubject.next(false);
  }

  public setFilter(filter) {
    this._tableFilterSubject.next(filter);
  }

  /** Get group key value from item; supports FormGroup (item.value) and plain objects. */
  private _getItemGroupValue(item: any, groupKey: string): any {
    if (item == null) return undefined;
    if (item.value && typeof item.value === 'object' && Object.prototype.hasOwnProperty.call(item.value, groupKey)) {
      return item.value[groupKey];
    }
    return item[groupKey];
  }

  private _multiSort(data: any[], sortBy: string[], getGroupValue?: (item: any, key: string) => any): any[] {
    if (!sortBy || sortBy.length === 0) {
      return data;
    }
    const getVal = getGroupValue || ((item: any, key: string) => item[key]);

    return data.sort((a, b) => {
      for (const key of sortBy) {
        const aValue = getVal(a, key);
        const bValue = getVal(b, key);

        if (aValue < bValue) {
          return -1;
        }
        if (aValue > bValue) {
          return 1;
        }
      }
      return 0;
    });
  }

  public iterativeGroup(data: any[], groupByColumns: IGroupByColumn[]): any[] {
    if (
      !groupByColumns ||
      groupByColumns.length === 0 ||
      !data ||
      data.length === 0
    ) {
      return data;
    }

    const finalResult: any[] = [];

    // First Pass: Build the finalResult array with groups and data items in order
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const previousItem = i > 0 ? data[i - 1] : null;

      // Determine the highest level where the group value changes
      let highestChangedLevel = -1;
      for (let l = 0; l < groupByColumns.length; l++) {
        const groupKey = groupByColumns[l].key;
        const itemVal = this._getItemGroupValue(item, groupKey);
        const prevVal = previousItem ? this._getItemGroupValue(previousItem, groupKey) : undefined;
        if (i === 0 || (previousItem && prevVal !== itemVal)) {
          highestChangedLevel = l;
        }
      }
      // console.log('iterativeGroup: Item', i, '- highestChangedLevel:', highestChangedLevel, 'groupKey:', groupByColumns[highestChangedLevel]?.key, 'itemValue:', item[groupByColumns[highestChangedLevel]?.key]);

      // If there was a group change, insert new group headers
      if (highestChangedLevel !== -1) {
        // Find the parent group in the already built finalResult for the highestChangedLevel
        // This logic is needed to set the parent property on the Group objects themselves
        let currentParentGroup: Group | null = null;
        if (highestChangedLevel > 0) {
          for (let j = finalResult.length - 1; j >= 0; j--) {
            const potentialParent = finalResult[j];
            if (
              potentialParent instanceof Group &&
              potentialParent.level === highestChangedLevel - 1
            ) {
              // Verify if this potential parent is indeed the correct parent based on grouping values up to its level
              let isCorrectParent = true;
              for (let k = 0; k <= potentialParent.level; k++) {
                const groupKey = groupByColumns[k].key;
                if (potentialParent[groupKey] !== this._getItemGroupValue(item, groupKey)) {
                  isCorrectParent = false;
                  break;
                }
              }
              if (isCorrectParent) {
                currentParentGroup = potentialParent;
                break; // Found the correct parent
              }
            }
          }
        }

        for (let m = highestChangedLevel; m < groupByColumns.length; m++) {
          const newGroup = new Group();
          newGroup.level = m;
          newGroup.parent = currentParentGroup as any; // Set parent relationship for Group objects
          newGroup.groupId = Guid.newGuid().toString(); // Generate a unique ID
          newGroup.groupFor = groupByColumns[m].key; // The key this group is based on

          // Set grouping values for the new group header (use _getItemGroupValue so FormGroup row data in .value is read)
          for (let n = 0; n <= m; n++) {
            const groupKey = groupByColumns[n].key;
            newGroup[groupKey] = this._getItemGroupValue(item, groupKey);
          }

          finalResult.push(newGroup); // Add the new group header
          // console.log('iterativeGroup: Created new group at level', m, ':', newGroup, 'Parent:', currentParentGroup);
          currentParentGroup = newGroup; // This new group is the parent for the next level down
        }
      }

      // Add the data item to the result (without setting parent in this pass yet)
      finalResult.push(item);
    }

    // Second Pass: Set parent property on data items and calculate counts
    const groupTotalCounts: { [groupId: string]: number } = {};

    // Initialize group counts
    finalResult.forEach((item) => {
      if (item instanceof Group && item.groupId) {
        groupTotalCounts[item.groupId] = 0; // Initialize count
      }
    });

    finalResult.forEach((item, index) => {
      if (!(item instanceof Group)) {
        // Find the most specific preceding group that this item belongs to
        let parentGroup: Group | null = null;
        for (let j = index - 1; j >= 0; j--) {
          const potentialParent = finalResult[j];
          if (potentialParent instanceof Group) {
            // Check if this group is an ancestor based on grouping keys
            let isAncestor = true;
            for (let l = 0; l <= potentialParent.level; l++) {
              const groupKey = groupByColumns[l].key;
              if (potentialParent[groupKey] !== this._getItemGroupValue(item, groupKey)) {
                isAncestor = false;
                break;
              }
            }
            if (isAncestor) {
              parentGroup = potentialParent;
              break; // Found the most specific ancestor group
            }
          }
        }
        // Set the group parent on the data item (use groupParent to avoid conflicting with FormGroup.parent which is read-only)
        (item as any).groupParent = parentGroup;
        // console.log('iterativeGroup: Item', item.id, 'assigned to parent group:', parentGroup?.groupId, 'GroupFor:', parentGroup?.groupFor, 'Value:', parentGroup ? parentGroup[parentGroup.groupFor] : null);

        // Increment count for this group and all its ancestors
        let currentAncestor = parentGroup;
        while (currentAncestor !== null) {
          if (currentAncestor.groupId) {
            groupTotalCounts[currentAncestor.groupId]++;
            // console.log('iterativeGroup: Incrementing count for group', currentAncestor.groupId, ', new count:', groupTotalCounts[currentAncestor.groupId]);
          }
          currentAncestor = currentAncestor.parent;
        }
      }
    });

    // Assign final calculated counts to the group objects
    finalResult.forEach((item) => {
      if (item instanceof Group && item.groupId) {
        item.totalCounts = groupTotalCounts[item.groupId] || 0;
        // console.log('iterativeGroup: Final count for group', item.groupId, ':', item.totalCounts);
      }
    });

    return finalResult;
  }

  // Merges newly scrolled data into the existing grouped data
  private _mergeScrolledData(
    newData: TItem[],
    formMappingOptions: IReactiveFormsDeepMappingOptions,
    groupByColumns: IGroupByColumn[]
  ): void {
    // console.log('DataTableDataSource._mergeScrolledData: START Merging (New Simplified Logic)');

    if (!newData || newData.length === 0) {
      this._dataSubject.next(this.data);
      this._loadingSubject.next(false);
      return;
    }

    const currentData = this.data; // This includes Group objects

    // Extract only the data items from the current view
    const currentDataItems = currentData.filter(
      (item) => !(item instanceof Group)
    );

    // Convert newData to FormGroups if needed
    const newDataAsType = this.toItemType(newData, formMappingOptions);

    // Combine current data items with new data items
    const combinedFlatData = [...currentDataItems, ...newDataAsType];

    // console.log('DataTableDataSource._mergeScrolledData: Combined flat data count:', combinedFlatData.length, 'CombinedFlatData:', combinedFlatData);

    // Prepare items will re-sort and re-apply grouping to the entire combined dataset
    const finalGroupedData = this.prepareItems(
      combinedFlatData,
      formMappingOptions,
      groupByColumns
    );

    // console.log('DataTableDataSource._mergeScrolledData: END Merging. FinalGroupedData:', finalGroupedData);
    this._dataSubject.next(finalGroupedData);
    this._loadingSubject.next(false);
  }

  public updatePageSize(pageSize: number) {
    this._pageSizeSubject.next(pageSize);
  }
}
